import { useMutation, useQuery } from '@tanstack/react-query';
import { Button, Card, Checkbox, Modal, TextInput } from 'flowbite-react';
import { CheckIcon } from 'lucide-react';
import { useCallback, useState } from 'react';
import { useForm } from 'react-hook-form';
import { Link } from 'react-router';

import { useApi } from '../../../api/useApi.hook';
import { ErrorSwitch } from '../../../components/errorMessage/errorSwitch.component';
import { DataPrivacyModal } from '../../modals/dataPrivacy/dataPrivacyModal.component';
import { TermsAndConditionsModal } from '../../modals/termsAndConditions/termsAndConditionsModal.component';
import { ProductLogo } from '../../components/productLogo/productLogo.component';
import { RouteIndex } from '../../routeIndex';

interface FormValues {
    email: string;
    tacAccepted: boolean;
    privacyAccepted: boolean;
}

const SignUpView = () => {
    const api = useApi();
    const [ wasLinkSent, setWasLinkSent ] = useState(false);
    const [ showTac, setShowTac ] = useState(false);
    const [ showPrivacy, setShowPrivacy ] = useState(false);
    const [ showGoogleConsent, setShowGoogleConsent ] = useState(false);
    const [ googleTac, setGoogleTac ] = useState(false);
    const [ googlePrivacy, setGooglePrivacy ] = useState(false);

    const { register, handleSubmit, watch, formState: { errors } } = useForm<FormValues>({
        defaultValues: { email: '', tacAccepted: false, privacyAccepted: false }
    });

    const tacAccepted = watch('tacAccepted');
    const privacyAccepted = watch('privacyAccepted');

    const { data: providers } = useQuery({
        queryKey: ['auth-providers'],
        queryFn: () => api.v1.auth.getProviders(),
    });
    const hasGoogle = providers?.some(p => p.providerName === 'google') ?? false;

    const doGoogleSignUp = useMutation({
        mutationFn: () => api.v1.auth.getGoogleLoginUrl(),
        onSuccess: ({ authUrl }) => { window.location.href = authUrl; },
    });

    const doLogin = useMutation({
        mutationFn: (email: string) => api.v1.auth.provider.magicLink.sendMagicLink(email, undefined, true, true),
        onSuccess: () => {
            setWasLinkSent(true);
        }
    });

    const onSubmit = useCallback((data: FormValues) => {
        doLogin.mutate(data.email.trim());
    }, [doLogin]);

    const resetState = useCallback(() => {
        setWasLinkSent(false);
    }, []);

    const openGoogleConsent = () => {
        setGoogleTac(false);
        setGooglePrivacy(false);
        setShowGoogleConsent(true);
    };

    const confirmGoogleConsent = () => {
        setShowGoogleConsent(false);
        doGoogleSignUp.mutate();
    };

    return (
        <div className="flex justify-center items-center min-h-screen bg-gray-100 p-2">
            <Card className="p-4 w-full max-w-sm shadow-lg">
                <ProductLogo className='text-center' />

                <div className='w-full border-t border-t-gray-200' />

                <h4 className="text-xl font-semibold text-center">Registrace</h4>
                <form onSubmit={handleSubmit(onSubmit)} className='flex flex-col gap-6'>
                    {wasLinkSent && (
                        <div className='flex flex-col items-center'>
                            <CheckIcon size={48} className='text-lime-700 mb-2' />
                            <h4 className="font-medium text-center mb-4">Registrační odkaz byl odeslán na vámi zadaný e-mail.</h4>
                            <div className='w-full border-t border-t-gray-200 mb-4' />
                            <p className='text-center text-sm mb-4'>Pokud odkaz nedorazil, zkontrolujte prosím složku Spam. Případně se zkuste <button onClick={resetState} className='hyperlink'>registrovat znovu.</button></p>
                            <p className='text-center text-sm font-semibold'>Tuto stránku nyní můžete zavřít.</p>
                        </div>
                    )}

                    {!wasLinkSent && (
                        <>
                            <div className="flex flex-col gap-4">
                                {hasGoogle && (
                                    <>
                                        <button
                                            type='button'
                                            onClick={openGoogleConsent}
                                            disabled={doGoogleSignUp.isPending}
                                            className='flex items-center justify-center gap-3 w-full py-2 px-4 border border-gray-300 rounded text-sm font-medium text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-60'
                                        >
                                            <svg width="18" height="18" viewBox="0 0 18 18" xmlns="http://www.w3.org/2000/svg">
                                                <path d="M17.64 9.2c0-.637-.057-1.251-.164-1.84H9v3.481h4.844c-.209 1.125-.843 2.078-1.796 2.717v2.258h2.908c1.702-1.567 2.684-3.875 2.684-6.615z" fill="#4285F4"/>
                                                <path d="M9 18c2.43 0 4.467-.806 5.956-2.184l-2.908-2.258c-.806.54-1.837.86-3.048.86-2.344 0-4.328-1.584-5.036-3.711H.957v2.332A8.997 8.997 0 0 0 9 18z" fill="#34A853"/>
                                                <path d="M3.964 10.707A5.41 5.41 0 0 1 3.682 9c0-.593.102-1.17.282-1.707V4.961H.957A8.996 8.996 0 0 0 0 9c0 1.452.348 2.827.957 4.039l3.007-2.332z" fill="#FBBC05"/>
                                                <path d="M9 3.58c1.321 0 2.508.454 3.44 1.345l2.582-2.58C13.463.891 11.426 0 9 0A8.997 8.997 0 0 0 .957 4.961L3.964 7.293C4.672 5.166 6.656 3.58 9 3.58z" fill="#EA4335"/>
                                            </svg>
                                            {doGoogleSignUp.isPending ? 'Přesměrovávám...' : 'Registrovat se přes Google'}
                                        </button>
                                        <div className='flex items-center gap-2'>
                                            <div className='flex-1 border-t border-gray-200' />
                                            <span className='text-xs text-gray-400'>nebo</span>
                                            <div className='flex-1 border-t border-gray-200' />
                                        </div>
                                    </>
                                )}

                                <div>
                                    <TextInput
                                        id="email"
                                        placeholder="Zadejte svůj e-mail"
                                        {...register('email', { required: 'E-mail je povinný', pattern: { value: /^\s*\S+@\S+\.\S+\s*$/, message: 'Zadali jste špatný formát e-mailu' } })}
                                        color={errors.email ? 'failure' : ''}
                                    />
                                    {errors.email && (
                                        <p className="text-sm text-red-500 mt-1">
                                            {errors.email.message}
                                        </p>
                                    )}
                                </div>

                                <ErrorSwitch
                                    className='text-center'
                                    error={doLogin.error}
                                    options={[
                                        { reasonCode: 'AccountDisabled', message: 'Váš účet byl deaktivován. Kontaktujte prosím podporu.' }
                                    ]}
                                    default={{ message: 'Nastala neočekávaná chyba. Zkus to prosím za chvíli znovu.' }}
                                />

                                <div className="flex items-start gap-2">
                                    <Checkbox id="tacAccepted" {...register('tacAccepted', { required: true })} className="mt-0.5 shrink-0" />
                                    <label htmlFor="tacAccepted" className="text-sm cursor-pointer">
                                        Souhlasím s <a onClick={() => setShowTac(true)} className="hyperlink cursor-pointer">obchodními podmínkami</a>
                                    </label>
                                </div>
                                <div className="flex items-start gap-2">
                                    <Checkbox id="privacyAccepted" {...register('privacyAccepted', { required: true })} className="mt-0.5 shrink-0" />
                                    <label htmlFor="privacyAccepted" className="text-sm cursor-pointer">
                                        Souhlasím se <a onClick={() => setShowPrivacy(true)} className="hyperlink cursor-pointer">zásadami ochrany osobních údajů</a>
                                    </label>
                                </div>
                                <Button
                                    type="submit"
                                    className="w-full"
                                    color="primary"
                                    disabled={doLogin.isPending || !tacAccepted || !privacyAccepted}
                                >
                                    {doLogin.isPending ? 'Odesílám...' : 'Registrovat se'}
                                </Button>
                                <p className="text-sm text-center text-gray-600 font-semibold">Na zadaný e-mail Vám zašleme odkaz pro dokončení registrace.</p>
                            </div>

                            <div className='w-full border-t border-t-gray-200' />

                            <div className="flex flex-col justify-center items-center gap-4">
                                <Link to={RouteIndex.login} className='hyperlink text-sm'>Zpět na přihlášení</Link>
                            </div>
                        </>
                    )}
                </form>
            </Card>

            <TermsAndConditionsModal show={showTac} onClose={() => setShowTac(false)} />
            <DataPrivacyModal show={showPrivacy} onClose={() => setShowPrivacy(false)} />

            <Modal show={showGoogleConsent} size="md" onClose={() => setShowGoogleConsent(false)}>
                <Modal.Header>Registrace přes Google</Modal.Header>
                <Modal.Body className='flex flex-col gap-4'>
                    <p className='text-sm text-gray-600'>Před pokračováním na Google prosím potvrďte souhlas s podmínkami.</p>
                    <div className="flex items-start gap-2">
                        <Checkbox
                            id="googleTac"
                            checked={googleTac}
                            onChange={e => setGoogleTac(e.target.checked)}
                            className="mt-0.5 shrink-0"
                        />
                        <label htmlFor="googleTac" className="text-sm cursor-pointer">
                            Souhlasím s <a onClick={() => setShowTac(true)} className="hyperlink cursor-pointer">obchodními podmínkami</a>
                        </label>
                    </div>
                    <div className="flex items-start gap-2">
                        <Checkbox
                            id="googlePrivacy"
                            checked={googlePrivacy}
                            onChange={e => setGooglePrivacy(e.target.checked)}
                            className="mt-0.5 shrink-0"
                        />
                        <label htmlFor="googlePrivacy" className="text-sm cursor-pointer">
                            Souhlasím se <a onClick={() => setShowPrivacy(true)} className="hyperlink cursor-pointer">zásadami ochrany osobních údajů</a>
                        </label>
                    </div>
                </Modal.Body>
                <Modal.Footer className='flex gap-2 justify-end'>
                    <Button color="gray" onClick={() => setShowGoogleConsent(false)}>Zrušit</Button>
                    <Button
                        color="dark"
                        disabled={!googleTac || !googlePrivacy}
                        onClick={confirmGoogleConsent}
                    >
                        Pokračovat na Google
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    );
};

export default SignUpView;
